import { useState, useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamificationApi, reportApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AgeGroupFilter from '../components/AgeGroupFilter';
import BibleFinisherBadge from '../components/BibleFinisherBadge';
import {
  INDIVIDUAL_BOARDS,
  INDIVIDUAL_BOARD_KEYS,
  isAgeGroupKey
} from '../utils/leaderboardConfig';
import './LeaderboardPage.css';

const emptyBoards = () => ({
  chapters: [],
  verses: [],
  words: [],
  weighted: [],
  family_chapters: []
});

const LeaderboardPage = () => {
  const { preferences } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const ageParam = searchParams.get('ageGroup') || '';
  const ageGroup = isAgeGroupKey(ageParam) ? ageParam : '';

  const [leaderboards, setLeaderboards] = useState(emptyBoards());
  const [unavailable, setUnavailable] = useState({});
  const [siteStats, setSiteStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (preferences?.competitiveMode) {
      loadLeaderboards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences, ageGroup]);

  const loadLeaderboards = async () => {
    try {
      if (leaderboards.chapters.length === 0 && !siteStats) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      const ageParams = ageGroup ? { ageGroup } : {};
      const [individualResults, familyChaptersRes] = await Promise.all([
        Promise.all(
          INDIVIDUAL_BOARD_KEYS.map((category) =>
            gamificationApi.getLeaderboard({ limit: 8, category, ...ageParams })
          )
        ),
        gamificationApi.getLeaderboard({ limit: 8, category: 'family_chapters' })
      ]);

      const next = emptyBoards();
      const missing = {};
      INDIVIDUAL_BOARD_KEYS.forEach((category, index) => {
        next[category] = individualResults[index].data.leaderboard;
        // The API reports available=false while a board's data is not loaded yet (Words).
        if (individualResults[index].data.available === false) missing[category] = true;
      });
      setUnavailable(missing);
      next.family_chapters = familyChaptersRes.data.leaderboard;
      setLeaderboards(next);

      // Site totals are a bonus; the boards still render if this fails.
      reportApi.getGlobalStats()
        .then((res) => setSiteStats(res.data.stats))
        .catch((statsError) => console.error('Error loading site totals:', statsError));
    } catch (error) {
      console.error('Error loading leaderboards:', error);
      setError('Failed to load leaderboards. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleAgeGroupChange = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key) next.set('ageGroup', key);
    else next.delete('ageGroup');
    setSearchParams(next, { replace: true });
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const fullListLink = (category) =>
    `/leaderboard/${category}${ageGroup ? `?ageGroup=${ageGroup}` : ''}`;

  if (!preferences) {
    return (
      <div className="leaderboard-page">
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  if (!preferences.competitiveMode) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="leaderboard-page">
        <LoadingSpinner size="large" message="Loading leaderboards..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="leaderboard-page">
        <div className="leaderboard-error">
          <h2>🏆 Leaderboard</h2>
          <p className="error-message">{error}</p>
          <button onClick={loadLeaderboards} className="retry-button">
            Try again
          </button>
        </div>
      </div>
    );
  }

  const renderIndividualBoard = (category) => {
    const board = INDIVIDUAL_BOARDS[category];
    const entries = leaderboards[category];

    // Word totals stay hidden until chapter word counts have been loaded on the server.
    if (unavailable[category]) {
      return null;
    }

    return (
      <div
        key={category}
        className={`leaderboard-section ${category === 'chapters' ? 'leaderboard-section--featured' : ''}`}
      >
        <div className="section-header">
          <h3>{board.title}</h3>
          <Link to={fullListLink(category)} className="view-full-link">
            View full list →
          </Link>
        </div>
        {category === 'weighted' && (
          <p className="section-note">{board.description}</p>
        )}
        <div className="leaderboard-list">
          {entries.length > 0 ? (
            entries.map((entry) => (
              <div key={entry.reader.id} className="leaderboard-item">
                <div className="rank">{getRankIcon(entry.rank)}</div>
                <div className="player-info">
                  <div className="player-name player-name--with-badge">
                    <span className="player-name-text">
                      {!entry.hasPublicProfile ? entry.reader.name : (
                        <Link to={`/u/${entry.reader.id}`}>{entry.reader.name}</Link>
                      )}
                    </span>
                    {entry.completedBible && <BibleFinisherBadge />}
                  </div>
                </div>
                <div className="chapter-count">
                  <span className="chapter-count-number">{formatNumber(entry[category])}</span>
                  <span className="chapter-count-label">{board.valueLabel}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-leaderboard">No data available</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-header">
        <h1>🏆 Leaderboard</h1>
        <p>Every chapter counts.</p>
        <AgeGroupFilter value={ageGroup} onChange={handleAgeGroupChange} />
      </div>

      {siteStats && (
        <section className="site-totals" aria-label="Community totals">
          <div className="site-totals-item">
            <div className="site-totals-number">{formatNumber(siteStats.totalChaptersRead)}</div>
            <div className="site-totals-label">chapters</div>
          </div>
          <div className="site-totals-item">
            <div className="site-totals-number">{formatNumber(siteStats.totalReaders)}</div>
            <div className="site-totals-label">readers</div>
          </div>
          <div className="site-totals-item">
            <div className="site-totals-number">{formatNumber(siteStats.completeBibleReads)}</div>
            <div className="site-totals-label">
              {siteStats.completeBibleReads === 1 ? 'completion' : 'completions'}
            </div>
          </div>
          <div className="site-totals-item">
            <div className="site-totals-number">{formatNumber(siteStats.readersCompletedBible)}</div>
            <div className="site-totals-label">finished the Bible</div>
          </div>
        </section>
      )}

      <div className={`leaderboard-content ${refreshing ? 'is-refreshing' : ''}`}>
        <div className="leaderboard-grid">
          {INDIVIDUAL_BOARD_KEYS.map(renderIndividualBoard)}

          <div className="leaderboard-section">
            <div className="section-header">
              <h3>📚 Family chapters</h3>
              <Link to="/leaderboard/family_chapters" className="view-full-link">
                View full list →
              </Link>
            </div>
            <div className="leaderboard-list">
              {leaderboards.family_chapters.length > 0 ? (
                leaderboards.family_chapters.map((entry) => (
                  <div key={entry.family?.id || entry.id} className="leaderboard-item">
                    <div className="rank">{getRankIcon(entry.rank)}</div>
                    <div className="player-info">
                      <div className="player-name">{entry.family?.name || 'Unknown family'}</div>
                      {entry.family?.memberCount > 0 && (
                        <div className="player-stats">
                          <span className="member-count">{entry.family.memberCount} members</span>
                        </div>
                      )}
                    </div>
                    <div className="chapter-count">
                      <span className="chapter-count-number">{formatNumber(entry.chapters)}</span>
                      <span className="chapter-count-label">chapters</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-leaderboard">No data available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
